<script setup>
  import { toRefs, computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { Select } from "@/components/episto-ui/select";
  import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Label } from "@/components/ui/label";
  import { usePricerProjectStore } from "@stores/pricer/pricerProject";

  const props = defineProps({
    field: { type: Object, required: true },
  });

  const { field } = toRefs(props);

  const projectStore = usePricerProjectStore();
  const { selectedTarget: target } = storeToRefs(projectStore);

  const filteredOptions = computed(() => {
    if (field.value.type !== "select" || !field.value.options) return [];

    return field.value.options.filter((option) => {
      if (!option.conditions) return true;

      return option.conditions.every((condition) => {
        return target.value[condition.field] === condition.value;
      });
    });
  });
</script>

<template>
  <div class="w-full">
    <Input
      v-if="['text', 'number'].includes(field.type)"
      v-model="target[field.name]"
      :class="{ 'w-24': field.type === 'number' && field.max != null && field.max <= 100 }"
      :type="field.type === 'number' ? 'number' : 'text'"
      :placeholder="field.placeholder"
      :min="field.min"
      :max="field.max" />
    <Textarea
      v-else-if="field.type === 'textarea'"
      v-model="target[field.name]"
      :placeholder="field.placeholder" />
    <Select
      v-else-if="field.type === 'select'"
      v-model="target[field.name]"
      :options="filteredOptions"
      :placeholder="field.placeholder" />
    <RadioGroup
      v-else-if="field.type === 'custom-radio'"
      v-model="target[field.name]"
      class="flex w-full gap-4">
      <div
        v-for="option in field.options"
        :key="option.value"
        class="flex-1">
        <RadioGroupItem
          :id="`${field.name}-${option.value}`"
          :value="option.value"
          class="peer sr-only" />
        <Label
          :for="`${field.name}-${option.value}`"
          class="flex w-full items-center justify-center rounded-md border-2 border-muted bg-popover py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer transition-all">
          <span class="text-sm font-medium">{{ option.label }}</span>
        </Label>
      </div>
    </RadioGroup>
    <div
      v-else-if="field.type === 'custom-check'"
      class="flex w-full gap-4">
      <div
        v-for="option in field.options"
        :key="option.value"
        class="flex-1">
        <Checkbox
          :id="`${field.name}-${option.value}`"
          v-model="target[field.name][option.value]"
          class="peer sr-only" />
        <Label
          :for="`${field.name}-${option.value}`"
          class="flex w-full items-center justify-center rounded-md border-2 border-muted bg-popover py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer transition-all">
          <span class="text-sm font-medium">{{ option.label }}</span>
        </Label>
      </div>
    </div>
    <div
      v-else-if="field.type === 'checkbox'"
      class="flex items-center space-x-2">
      <Checkbox
        :id="field.name"
        v-model="target[field.name]" />
      <Label :for="field.name">
        {{ field.label }}
      </Label>
    </div>
    <div
      v-else-if="field.type === 'none'"
      class="flex items-center space-x-2">
      <Label :for="field.name">
        {{ field.label }}
      </Label>
    </div>
  </div>
</template>
